import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import React from 'react';
import { PutClient } from '../../clients';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { AttachmentType, DocumentAttachment } from '../../types';
import { sanitizeFilenameISO88591 } from '../../utils/textUtils';
import { useTranslation } from '../blocks';
import { DocumentUploader } from './DocumentUploader';

type PurchaseOrderDocumentUploaderProps = {
    purchaseOrderId: string;
    shipmentId: string;
    username: string;
    onDocumentUploaded?: (document: DocumentAttachment) => void;
};

const PO_DOC_DESCRIPTION = 'Purchase order document uploaded from RP.';

export const PurchaseOrderDocumentUploader = ({
    purchaseOrderId,
    shipmentId,
    username,
    onDocumentUploaded,
}: PurchaseOrderDocumentUploaderProps) => {
    const {
        returnOrders: { requestAttachmentUploadUrl },
    } = useHrpsApi();
    const { t } = useTranslation('purchaseOrderViewPage');

    const uploadPurchaseOrderDocument = async (file: File): Promise<DocumentAttachment> => {
        const uploadUrlResponse = await requestAttachmentUploadUrl({
            key1Value: purchaseOrderId,
            key2Value: shipmentId,
            filename: sanitizeFilenameISO88591(file.name),
            type: AttachmentType.SALES_ORDER_DOCUMENT_UPLOAD,
            mimeType: file.type,
            description: PO_DOC_DESCRIPTION,
            visibleToReseller: true,
        });

        await PutClient.put(uploadUrlResponse.url, file);

        return {
            attachmentId: uploadUrlResponse.id,
            attachmentName: file.name,
            attachmentUser: username,
        };
    };

    return (
        <Box type={'outline'} spacingInset={'none'} width={'100%'} data-testid={'purchaseOrderDocumentUploader'}>
            <Box spacingInset={'medium'} backgroundColor={'secondary'}>
                <Text type={'h200'}>{t('documentUploader-title')}</Text>
            </Box>
            <Box spacingInset={'500'}>
                <Column alignmentHorizontal={'center'}>
                    <DocumentUploader
                        description={t('documentUploader-description')}
                        acceptedFileFormats={'.pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv'}
                        uploadButtonLabel={t('documentUploader-buttonLabel')}
                        onUpload={uploadPurchaseOrderDocument}
                        onDocumentUploaded={onDocumentUploaded}
                    />
                </Column>
            </Box>
        </Box>
    );
};
